import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Hero } from "@/components/site/Hero";
import { EditorialMarquee } from "@/components/site/Marquee";
import { ContactForm } from "@/components/site/ContactForm";
import { Reveal } from "@/components/site/Reveal";
import { SERVICES, STAFF, DIVISIONS, slugify } from "@/lib/data";

const initials = (name) =>
  name.replace(/&/g, " ").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

// Order the overview to match the Team page division order.
const _byName = Object.fromEntries(STAFF.map((p) => [p.name, p]));
const ORDERED_STAFF = DIVISIONS.flatMap((d) => d.members).map((n) => _byName[n]).filter(Boolean);

const Services = () => (
  <section id="services" data-testid="services" className="relative py-28 lg:py-40 bg-[#0a0a0a] border-y border-white/10">
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
      <Reveal className="mb-20 max-w-3xl">
        <span className="text-[12px] uppercase tracking-[0.32em] text-[#cbd1d8]">What We Do</span>
        <h2 className="font-display text-6xl lg:text-8xl leading-[0.88] mt-5 text-white">Full-service<br />representation.</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {SERVICES.map((s, i) => {
          const light = [0, 3, 4].includes(i);
          return (
            <Reveal key={s.n} delay={i * 0.06}>
              <div className={`group p-10 lg:p-14 h-full ${light ? "bg-[#cbd1d8]" : "bg-[#0a0a0a]"}`}>
                <div className="flex items-start justify-between mb-8">
                  <span className={`font-display text-5xl ${light ? "text-black/40" : "text-white/20"}`}>{s.n}</span>
                </div>
                <h3 className={`font-display text-3xl lg:text-4xl mb-4 ${light ? "text-black" : "text-white"}`}>{s.title}</h3>
                <p className={`text-lg leading-relaxed ${light ? "text-black/70" : "text-white/50"}`}>{s.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const TeamPreview = () => (
  <section id="team" data-testid="team-preview" className="relative py-28 lg:py-40 bg-[#050505]">
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
      <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
        <div>
          <span className="text-[12px] uppercase tracking-[0.32em] text-[#cbd1d8]">The People</span>
          <h2 className="font-display text-6xl lg:text-8xl leading-[0.88] mt-5 text-white">Meet the team.</h2>
          <p className="text-white/50 text-lg mt-6 max-w-xl leading-relaxed">
            The giant-slayers behind every deal — negotiators, strategists, marketers and partners who put athletes and families first.
          </p>
        </div>
        <Link
          to="/team"
          data-testid="team-preview-cta"
          className="group inline-flex items-center gap-3 border border-white/25 text-white font-bold uppercase tracking-[0.14em] text-sm px-8 py-4 hover:border-[#cbd1d8] hover:text-[#cbd1d8] transition-colors duration-300 shrink-0"
        >
          View Full Bios <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </Reveal>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
        {ORDERED_STAFF.map((p, i) => (
          <Reveal key={p.name} delay={(i % 4) * 0.05}>
            <Link to={`/team#${slugify(p.name)}`} data-testid={`team-preview-${i}`} className="group block">
              <div className="relative aspect-[4/5] overflow-hidden bg-[#111] border border-white/10">
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-700 ease-out"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="font-display text-6xl text-white/15 group-hover:text-[#cbd1d8] transition-colors duration-500">{initials(p.name)}</span>
                  </div>
                )}
              </div>
              <h3 className="font-display text-2xl lg:text-3xl text-white mt-4 leading-none group-hover:text-[#cbd1d8] transition-colors duration-300">{p.name}</h3>
              <p className="text-[#cbd1d8] uppercase tracking-[0.16em] text-[11px] mt-2">{p.role}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <Services />
      <TeamPreview />
      <ContactForm />
    </div>
  );
}
